export const TASK_STATUS_OPTIONS = ["Pending", "In Progress", "Waiting", "Completed", "Cancelled"];

export const TASK_PRIORITY_OPTIONS = ["Low", "Medium", "High", "Urgent"];

const TASK_HISTORY_ACTION_LABELS = {
  Created: "Task Created",
  Assigned: "Task Assigned",
  Updated: "Task Updated",
  ProgressChanged: "Progress Updated",
  StatusChanged: "Status Updated",
  Forwarded: "Task Forwarded",
};

export const normalizeTaskStatus = (value) => {
  const normalized = String(value || "").trim().toLowerCase();

  if (normalized === "forwarded") return "Forwarded";
  if (normalized === "in progress") return "In Progress";
  if (normalized === "waiting" || normalized === "on hold") return "Waiting";
  if (normalized === "completed" || normalized === "done" || normalized === "resolved") return "Completed";
  if (normalized === "cancelled" || normalized === "canceled") return "Cancelled";
  return "Pending";
};

export const normalizeTaskPriority = (value) => {
  const normalized = String(value || "").trim().toLowerCase();

  if (normalized === "urgent") return "Urgent";
  if (normalized === "high") return "High";
  if (normalized === "medium") return "Medium";
  return "Low";
};

export const normalizeTaskProgressPercent = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.min(100, Math.round(parsed)));
};

export const resolveTaskProgressState = (status, progressPercent) => {
  const normalizedProgress = normalizeTaskProgressPercent(progressPercent);
  const normalizedStatus = normalizeTaskStatus(status);

  if (normalizedStatus === "Completed" || normalizedProgress >= 100) {
    return {
      status: "Completed",
      progressPercent: 100,
    };
  }

  return {
    status: normalizedStatus,
    progressPercent: normalizedProgress,
  };
};

export const normalizeTaskHistoryActionType = (value) => {
  const normalized = String(value || "").trim().toLowerCase();

  if (normalized === "created" || normalized === "task created") return "Created";
  if (normalized === "assigned" || normalized === "task assigned") return "Assigned";
  if (normalized === "forwarded" || normalized === "task forwarded" || normalized === "forward") return "Forwarded";
  if (normalized === "progresschanged" || normalized === "progress changed" || normalized === "progress updated") return "ProgressChanged";
  if (normalized === "statuschanged" || normalized === "status changed" || normalized === "status updated" || normalized === "completed") {
    return "StatusChanged";
  }

  return "Updated";
};

export const getTaskHistoryActionLabel = (actionType, actionLabel) =>
  String(actionLabel || "").trim() || TASK_HISTORY_ACTION_LABELS[normalizeTaskHistoryActionType(actionType)] || "Task Updated";

export const getTaskStatusClasses = (status) => {
  const normalized = normalizeTaskStatus(status);

  if (normalized === "Forwarded") {
    return "border border-violet-200 bg-violet-100 text-violet-700";
  }

  if (normalized === "Assigned") {
    return "border border-sky-200 bg-sky-100 text-sky-700";
  }

  if (normalized === "Completed") {
    return "border border-emerald-200 bg-emerald-100 text-emerald-700";
  }

  if (normalized === "In Progress") {
    return "border border-blue-200 bg-blue-100 text-blue-700";
  }

  if (normalized === "Waiting") {
    return "border border-amber-200 bg-amber-100 text-amber-700";
  }

  if (normalized === "Cancelled") {
    return "border border-rose-200 bg-rose-100 text-rose-700";
  }

  return "border border-slate-200 bg-slate-100 text-slate-700";
};

export const getTaskHistoryActionClasses = (actionType) => {
  const normalized = normalizeTaskHistoryActionType(actionType);

  if (normalized === "Forwarded") {
    return "border border-violet-200 bg-violet-100 text-violet-700";
  }

  if (normalized === "StatusChanged") {
    return "border border-blue-200 bg-blue-100 text-blue-700";
  }

  if (normalized === "ProgressChanged") {
    return "border border-cyan-200 bg-cyan-100 text-cyan-700";
  }

  if (normalized === "Created") {
    return "border border-emerald-200 bg-emerald-100 text-emerald-700";
  }

  return "border border-slate-200 bg-slate-100 text-slate-700";
};

export const getTaskPriorityClasses = (priority) => {
  const normalized = normalizeTaskPriority(priority);

  if (normalized === "Urgent") {
    return "border border-rose-200 bg-rose-100 text-rose-700";
  }

  if (normalized === "High") {
    return "border border-orange-200 bg-orange-100 text-orange-700";
  }

  if (normalized === "Medium") {
    return "border border-amber-200 bg-amber-100 text-amber-700";
  }

  return "border border-emerald-200 bg-emerald-100 text-emerald-700";
};

export const formatTaskDate = (value, withTime = false) => {
  if (!value) return "Not set";

  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
    }).format(new Date(value));
  } catch {
    return value;
  }
};

export const toDateInputValue = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().split("T")[0];
};

export const sortTasksByRecent = (items) =>
  [...(items || [])].sort((a, b) => {
    const aTime = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
    const bTime = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
    return bTime - aTime;
  });

export const sortTaskHistoryByRecent = (items) =>
  [...(items || [])].sort((a, b) => {
    const aTime = new Date(a?.createdAt || 0).getTime();
    const bTime = new Date(b?.createdAt || 0).getTime();
    return bTime - aTime;
  });

export const isTaskForwardHistoryItem = (item) => normalizeTaskHistoryActionType(item?.actionType || item?.actionLabel) === "Forwarded";

export const getTaskForwardCount = (task) =>
  (task?.history || []).filter((item) => normalizeTaskStatus(item?.status) === "Forwarded" || isTaskForwardHistoryItem(item)).length;

export const getTaskCurrentTimelineItem = (task) => {
  const assignedUserId = String(task?.assignedUserId || "").trim();
  const assignedEmployeeId = String(task?.assignedEmployeeId || "").trim();

  return sortTaskHistoryByRecent(task?.history || []).find((item) => {
    const itemUserId = String(item?.toAssignedUserId || "").trim();
    const itemEmployeeId = String(item?.toAssignedEmployeeId || "").trim();

    return (assignedUserId && itemUserId === assignedUserId) || (assignedEmployeeId && itemEmployeeId === assignedEmployeeId);
  }) || null;
};

export const getTaskCurrentOwner = (task) => {
  const timelineItem = getTaskCurrentTimelineItem(task);

  return {
    assignedUserId: String(timelineItem?.toAssignedUserId || task?.assignedUserId || "").trim(),
    assignedEmployeeId: String(timelineItem?.toAssignedEmployeeId || task?.assignedEmployeeId || "").trim(),
    assignedEmployeeName: String(timelineItem?.toAssignedEmployeeName || task?.assignedEmployeeName || "").trim(),
  };
};

export const shouldShowInMyTasks = (task, userId) => {
  const normalizedUserId = String(userId || "").trim();
  if (!normalizedUserId) return false;

  const createdByUserId = String(task?.createdByUserId || "").trim();
  if (createdByUserId && createdByUserId === normalizedUserId) {
    return true;
  }

  const currentOwner = getTaskCurrentOwner(task);
  return String(currentOwner.assignedUserId || "").trim() === normalizedUserId;
};

export const getTaskCurrentTimelineStatus = (task) => {
  const timelineItem = getTaskCurrentTimelineItem(task);
  return normalizeTaskStatus(timelineItem?.status || task?.status);
};

export const isTaskForwardLocked = (task) => getTaskCurrentTimelineStatus(task) === "Forwarded";

export const isTaskCompleted = (taskOrStatus) =>
  normalizeTaskStatus(
    typeof taskOrStatus === "string" ? taskOrStatus : taskOrStatus?.status
  ) === "Completed";

export const isTaskFullyCompleted = (task) =>
  isTaskCompleted(task) && normalizeTaskProgressPercent(task?.progressPercent) === 100;
